import React, { useState, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from './cropUtils'; // Ensure this file exists

const SchoolStoriesTab = () => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(false);

    // Edit Form State
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', designation: '', school_name: '', city: '', message: '', status: 'pending' });
    
    // Image & Cropping State
    const [imageSrc, setImageSrc] = useState(null); // Source for cropper
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedImageBlob, setCroppedImageBlob] = useState(null); // Final blob to upload
    const [isCropping, setIsCropping] = useState(false);
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchStories();
    }, []);

    const fetchStories = async () => {
        try {
            const res = await fetch('/api/admin/stories');
            const data = await res.json();
            setStories(data);
        } catch (error) {
            console.error("Error fetching stories:", error);
        }
    };

    // --- QUICK STATUS UPDATE (APPROVE / REJECT) ---
    const handleQuickStatus = async (id, newStatus, currentData) => {
        if (!window.confirm(`Are you sure you want to mark this as ${newStatus}?`)) return;
        
        // Use existing PUT logic but strictly for status
        const data = new FormData();
        data.append('name', currentData.name);
        data.append('designation', currentData.designation);
        data.append('school_name', currentData.school_name || '');
        data.append('city', currentData.city || '');
        data.append('message', currentData.message);
        data.append('status', newStatus);
        
        try {
            const res = await fetch(`/api/admin/stories/${id}`, {
                method: 'PUT',
                body: data
            });
            if (res.ok) fetchStories();
        } catch (err) {
            console.error(err);
        }
    };

    // --- EDIT & FORM HANDLING ---
    const handleEditClick = (s) => {
        setEditingId(s.id);
        setFormData({
            name: s.name,
            designation: s.designation,
            school_name: s.school_name || '',
            city: s.city || '',
            message: s.message,
            status: s.status
        });
        setCroppedImageBlob(null);
        setImageSrc(null);
        setIsCropping(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ name: '', designation: '', school_name: '', city: '', message: '', status: 'pending' });
        setCroppedImageBlob(null);
        setImageSrc(null);
        setIsCropping(false);
    };

    // --- CROPPER LOGIC ---
    const onFileChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result);
                setIsCropping(true);
            });
            reader.readAsDataURL(file);
        }
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const showCroppedImage = async () => {
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            setCroppedImageBlob(croppedBlob);
            setIsCropping(false); // Close cropper, show preview
        } catch (e) {
            console.error(e);
        }
    };

    // --- SUBMIT UPDATE ---
    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = new FormData();
        Object.keys(formData).forEach(key => data.append(key, formData[key]));
        
        // If a new cropped image exists, send it
        if (croppedImageBlob) {
            // Create a file from blob
            const file = new File([croppedImageBlob], "profile_updated.webp", { type: "image/webp" });
            data.append('image', file);
        }

        try {
            const res = await fetch(`/api/admin/stories/${editingId}`, {
                method: 'PUT',
                body: data
            });
            if (res.ok) {
                fetchStories();
                handleCancelEdit();
            } else {
                alert("Update failed");
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently delete this submission?")) return;
        await fetch(`/api/admin/stories/${id}`, { method: 'DELETE' });
        fetchStories();
    };

    const getStatusColor = (status) => {
        if (status === 'approved') return 'bg-green-100 text-green-700 border-green-200';
        if (status === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    };

    return (
        <div className="space-y-8 relative">
            
            {/* --- CROPPER MODAL (Overlay) --- */}
            {isCropping && imageSrc && (
                <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
                    <div className="relative w-full max-w-lg h-96 bg-slate-800 rounded-lg overflow-hidden border border-slate-600">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1} // 1:1 Square aspect ratio
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div className="w-full max-w-lg mt-4 flex items-center gap-4">
                        <span className="text-white text-xs font-bold">Zoom</span>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(e.target.value)}
                            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div className="mt-6 flex gap-4">
                        <button onClick={() => { setIsCropping(false); setImageSrc(null); if(fileInputRef.current) fileInputRef.current.value=''; }} className="px-6 py-2 bg-slate-600 text-white rounded-full font-bold uppercase text-xs hover:bg-slate-500">
                            Cancel
                        </button>
                        <button onClick={showCroppedImage} className="px-8 py-2 bg-brand-orange text-white rounded-full font-bold uppercase text-xs hover:bg-orange-600">
                            Crop & Save
                        </button>
                    </div>
                </div>
            )}

            {/* --- EDIT FORM --- */}
            {editingId && !isCropping && (
                <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-blue-100 mb-8 animate-in fade-in slide-in-from-top-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-black text-blue-800 uppercase">Edit Submission</h3>
                        <button onClick={handleCancelEdit} className="text-red-500 text-xs font-bold hover:underline">Cancel</button>
                    </div>
                    <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Status</label>
                            <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full p-2 border rounded font-bold bg-slate-50">
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                        <div><label className="text-xs font-bold text-slate-400 uppercase">Name</label><input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2 border rounded" /></div>
                        <div><label className="text-xs font-bold text-slate-400 uppercase">Designation</label><input required value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} className="w-full p-2 border rounded" /></div>
                        <div><label className="text-xs font-bold text-slate-400 uppercase">School Name</label><input value={formData.school_name} onChange={e => setFormData({...formData, school_name: e.target.value})} className="w-full p-2 border rounded" placeholder="For Schools" /></div>
                        <div><label className="text-xs font-bold text-slate-400 uppercase">City</label><input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} className="w-full p-2 border rounded" placeholder="For Job Seekers" /></div>
                        <div className="col-span-2"><label className="text-xs font-bold text-slate-400 uppercase">Message</label><textarea required value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="w-full p-2 border rounded h-24" /></div>
                        
                        {/* PHOTO UPLOAD WITH PREVIEW */}
                        <div className="col-span-2">
                            <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Update Photo</label>
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 bg-slate-100 rounded-full overflow-hidden border-2 border-slate-200">
                                    {croppedImageBlob ? (
                                        <img src={URL.createObjectURL(croppedImageBlob)} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">Old IMG</div>
                                    )}
                                </div>
                                <div>
                                    <input 
                                        ref={fileInputRef} 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={onFileChange} 
                                        className="text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">Select image to open Cropper tool.</p>
                                </div>
                            </div>
                        </div>

                        <div className="col-span-2 mt-4"><button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold uppercase hover:bg-blue-700">{loading ? 'Updating...' : 'Save Changes'}</button></div>
                    </form>
                </div>
            )}

            {/* --- LIST VIEW --- */}
            <div className="grid grid-cols-1 gap-4">
                {stories.length === 0 && <p className="text-center text-slate-400">No stories submitted yet.</p>}
                {stories.map((s) => (
                    <div key={s.id} className={`bg-white p-6 rounded-2xl shadow-sm border relative group flex flex-col md:flex-row gap-6 ${s.status === 'pending' ? 'border-yellow-400 ring-1 ring-yellow-400 bg-yellow-50/50' : 'border-slate-100'}`}>
                        <div className="shrink-0 flex flex-col items-center">
                            <img src={`${s.image_path}?t=${Date.now()}`} alt={s.name} className="w-20 h-20 rounded-full object-cover shadow-md bg-white border-4 border-white" />
                            <span className={`mt-3 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${getStatusColor(s.status)}`}>{s.status}</span>
                        </div>
                        <div className="flex-1">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-lg text-slate-900">{s.name}</h4>
                                    <p className="text-xs font-bold text-brand-orange uppercase">{s.designation}</p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {s.user_type === 'school' ? `🏫 ${s.school_name}` : `📍 ${s.city}`}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    {/* Edit Button */}
                                    <button onClick={() => handleEditClick(s)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full" title="Edit & Crop"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                                    
                                    {/* Delete Button */}
                                    <button onClick={() => handleDelete(s.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full" title="Delete"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                </div>
                            </div>
                            
                            <div className="mt-4 bg-white/60 p-4 rounded-xl text-sm text-slate-600 italic border border-slate-200">"{s.message}"</div>
                            
                            {/* --- QUICK ACTION BUTTONS FOR PENDING ITEMS --- */}
                            {s.status === 'pending' && (
                                <div className="mt-4 flex gap-3 pt-3 border-t border-slate-200/50">
                                    <button onClick={() => handleQuickStatus(s.id, 'approved', s)} className="flex-1 bg-green-500 text-white py-2 rounded-lg text-xs font-bold uppercase hover:bg-green-600 shadow-sm flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        Approve Now
                                    </button>
                                    <button onClick={() => handleQuickStatus(s.id, 'rejected', s)} className="px-4 bg-red-100 text-red-600 border border-red-200 py-2 rounded-lg text-xs font-bold uppercase hover:bg-red-200">
                                        Reject
                                    </button>
                                </div>
                            )}

                            <div className="mt-2 text-[10px] text-slate-400 text-right">Submitted: {new Date(s.submitted_at).toLocaleDateString()}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SchoolStoriesTab;